import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import PageHeader from '../components/common/PageHeader';
import { listLogs, getLog } from '../services/logsApi';

const KIND_LABELS = {
  'sync.bulk-everything': 'Bulk sync everything',
  'products.bulk-sync': 'Bulk product sync',
  'fabrics.bulk-push': 'Fabrics bulk push',
  'finishes.bulk-push': 'Finishes bulk push',
  'image-migration': 'Image migration',
};

const statusColor = (status) =>
  status === 'done' ? '#00A878' :
  status === 'running' ? '#6C63FF' :
  status === 'error' ? '#EF4444' : '#888';

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString() : '—');
const fmtDuration = (start, end) => {
  if (!start || !end) return '—';
  const ms = new Date(end) - new Date(start);
  if (ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const summarizeTotals = (totals) => {
  if (!totals || typeof totals !== 'object') return '—';
  const parts = [];
  if (totals.synced !== undefined) parts.push(`${totals.synced} synced`);
  if (totals.errors) parts.push(`${totals.errors} errors`);
  if (totals.total !== undefined && totals.synced === undefined) parts.push(`${totals.total} total`);
  return parts.length > 0 ? parts.join(' · ') : JSON.stringify(totals);
};

const Logs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listLogs({ limit: 100 });
      setItems(rows);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const item = await getLog(id);
      setDetail(item);
    } catch (err) {
      console.error('Failed to load log:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Logs"
        subtitle="History of push and sync runs"
        action={
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
            onClick={fetchItems}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kind</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Totals</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && !loading && (
                <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" sx={{ p: 3 }}>No log entries yet.</Typography></TableCell></TableRow>
              )}
              {items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{fmtTime(row.started_at)}</TableCell>
                  <TableCell>{KIND_LABELS[row.kind] || row.kind}</TableCell>
                  <TableCell>{row.shop_domain || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{ bgcolor: `${statusColor(row.status)}20`, color: statusColor(row.status), fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{summarizeTotals(row.totals)}</Typography>
                  </TableCell>
                  <TableCell>{fmtDuration(row.started_at, row.finished_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{row.triggered_by_email || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDetail(row.id)}><VisibilityRounded fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!detail || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {detail ? (KIND_LABELS[detail.kind] || detail.kind) : 'Loading…'}
        </DialogTitle>
        <DialogContent>
          {detailLoading && <CircularProgress />}
          {detail && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`Status: ${detail.status}`} sx={{ bgcolor: `${statusColor(detail.status)}20`, color: statusColor(detail.status) }} />
                <Chip variant="outlined" label={`Store: ${detail.shop_domain || '—'}`} />
                <Chip variant="outlined" label={`Started: ${fmtTime(detail.started_at)}`} />
                <Chip variant="outlined" label={`Duration: ${fmtDuration(detail.started_at, detail.finished_at)}`} />
                <Chip variant="outlined" label={`By: ${detail.triggered_by_email || '—'}`} />
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Totals</Typography>
              <Box component="pre" sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontSize: '0.85rem', overflow: 'auto', m: 0 }}>
                {JSON.stringify(detail.totals, null, 2)}
              </Box>

              {detail.phase_results && Object.keys(detail.phase_results).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Phase results</Typography>
                  <Box component="pre" sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontSize: '0.85rem', overflow: 'auto', m: 0 }}>
                    {JSON.stringify(detail.phase_results, null, 2)}
                  </Box>
                </>
              )}

              {Array.isArray(detail.results) && detail.results.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                    Errors / per-item ({detail.results.length})
                  </Typography>
                  <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                    {detail.results.map((r, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                        {r.phase ? <strong>[{r.phase}]</strong> : null}
                        {' '}
                        {r.entity_id || r.product_id ? `#${r.entity_id || r.product_id}` : ''}
                        {' — '}{r.message || JSON.stringify(r)}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}

              {detail.error_message && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: 'error.main' }}>Error</Typography>
                  <Typography variant="body2" color="error">{detail.error_message}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Logs;
